// Firebase Firestore 서비스 함수 - 최적화된 버전
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import bcrypt from 'bcryptjs'; // 🔒 비밀번호 검증용

// 컬렉션 이름 상수 - 제조사별 분리
const ERRORS_COLLECTIONS = {
  // 미쓰비시
  mitsubishi: {
    plc: 'mitsubishi_plc_errors',
    motion: 'mitsubishi_motion_errors',
    servo: 'mitsubishi_servo_errors',
    inverter: 'mitsubishi_inverter_errors', // Mitsubishi 인버터 에러 코드
  },
  // LS ELECTRIC
  'ls-electric': {
    plc: 'ls_electric_xgb_errors',
    motion: 'ls_electric_motion_errors', // XGF-PD1H 217개 에러 코드
    servo: 'ls_electric_servo_errors', // LS ELECTRIC Servo 에러 코드
    inverter: 'ls_electric_inverter_errors', // iG5A 23개 에러 코드
  },
};

const REVIEWS_COLLECTIONS = {
  // 미쓰비시
  mitsubishi: {
    plc: 'mitsubishi_plc_reviews',
    motion: 'mitsubishi_motion_reviews',
    servo: 'mitsubishi_servo_reviews',
    inverter: 'mitsubishi_inverter_reviews', // Mitsubishi 인버터 리뷰
  },
  // LS ELECTRIC
  'ls-electric': {
    plc: 'ls_electric_xgb_reviews',
    motion: 'ls_electric_motion_reviews', // XGF-PD1H 모션 리뷰
    servo: 'ls_electric_servo_reviews', // LS ELECTRIC Servo 리뷰
    inverter: 'ls_electric_inverter_reviews', // iG5A 인버터 리뷰
  },
};

const FEEDBACK_COLLECTION = 'feedback'; // 피드백 컬렉션 (통합)

// 🚀 최적화: 컬렉션 참조 캐싱 (한 번만 생성)
const errorsCollectionRefs = {
  mitsubishi: {
    plc: collection(db, ERRORS_COLLECTIONS.mitsubishi.plc),
    motion: collection(db, ERRORS_COLLECTIONS.mitsubishi.motion),
    servo: collection(db, ERRORS_COLLECTIONS.mitsubishi.servo),
    inverter: collection(db, ERRORS_COLLECTIONS.mitsubishi.inverter),
  },
  'ls-electric': {
    plc: collection(db, ERRORS_COLLECTIONS['ls-electric'].plc),
    motion: collection(db, ERRORS_COLLECTIONS['ls-electric'].motion),
    servo: collection(db, ERRORS_COLLECTIONS['ls-electric'].servo),
    inverter: collection(db, ERRORS_COLLECTIONS['ls-electric'].inverter),
  },
};
const reviewsCollectionRefs = {
  mitsubishi: {
    plc: collection(db, REVIEWS_COLLECTIONS.mitsubishi.plc),
    motion: collection(db, REVIEWS_COLLECTIONS.mitsubishi.motion),
    servo: collection(db, REVIEWS_COLLECTIONS.mitsubishi.servo),
    inverter: collection(db, REVIEWS_COLLECTIONS.mitsubishi.inverter),
  },
  'ls-electric': {
    plc: collection(db, REVIEWS_COLLECTIONS['ls-electric'].plc),
    motion: collection(db, REVIEWS_COLLECTIONS['ls-electric'].motion),
    servo: collection(db, REVIEWS_COLLECTIONS['ls-electric'].servo),
    inverter: collection(db, REVIEWS_COLLECTIONS['ls-electric'].inverter),
  },
};
const feedbackCollectionRef = collection(db, FEEDBACK_COLLECTION); // 피드백 컬렉션 참조

/**
 * 제조사와 제품 카테고리에 따라 올바른 에러 컬렉션 이름 반환
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'inverter')
 * @returns {string} - 컬렉션 이름
 */
function getErrorsCollectionName(manufacturerId = 'mitsubishi', productCategory = 'plc') {
  const manufacturer = ERRORS_COLLECTIONS[manufacturerId] || ERRORS_COLLECTIONS.mitsubishi;
  const collectionName = manufacturer[productCategory];

  if (!collectionName) {
    console.error(`Invalid productCategory: ${productCategory} for manufacturer: ${manufacturerId}`);
    return null;
  }

  return collectionName;
}

/**
 * 제조사와 제품 카테고리에 따라 올바른 리뷰 컬렉션 이름 반환
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'inverter')
 * @returns {string} - 컬렉션 이름
 */
function getReviewsCollectionName(manufacturerId = 'mitsubishi', productCategory = 'plc') {
  const manufacturer = REVIEWS_COLLECTIONS[manufacturerId] || REVIEWS_COLLECTIONS.mitsubishi;
  const collectionName = manufacturer[productCategory];

  if (!collectionName) {
    console.error(`Invalid productCategory: ${productCategory} for manufacturer: ${manufacturerId}`);
    return null;
  }

  return collectionName;
}

/**
 * 제조사와 제품 카테고리에 따라 올바른 에러 컬렉션 참조 반환
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'inverter')
 * @returns {CollectionReference} - 컬렉션 참조
 */
function getErrorsCollectionRef(manufacturerId = 'mitsubishi', productCategory = 'plc') {
  // 캐시된 참조가 있으면 반환
  const manufacturerRefs = errorsCollectionRefs[manufacturerId] || errorsCollectionRefs.mitsubishi;
  if (manufacturerRefs[productCategory]) {
    return manufacturerRefs[productCategory];
  }

  // 캐시가 없으면 새로 생성
  const collectionName = getErrorsCollectionName(manufacturerId, productCategory);

  if (!collectionName) {
    return null;
  }

  return collection(db, collectionName);
}

/**
 * 제조사와 제품 카테고리에 따라 올바른 리뷰 컬렉션 참조 반환
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'inverter')
 * @returns {CollectionReference} - 컬렉션 참조
 */
function getReviewsCollectionRef(manufacturerId = 'mitsubishi', productCategory = 'plc') {
  // 캐시된 참조가 있으면 반환
  const manufacturerRefs = reviewsCollectionRefs[manufacturerId] || reviewsCollectionRefs.mitsubishi;
  if (manufacturerRefs[productCategory]) {
    return manufacturerRefs[productCategory];
  }

  // 캐시가 없으면 새로 생성
  const collectionName = getReviewsCollectionName(manufacturerId, productCategory);
  return collection(db, collectionName);
}

/**
 * 언어별 필드 추출 헬퍼 함수
 * @param {Object} data - Firestore 문서 데이터
 * @param {string} language - 언어 코드 ('ko' | 'en' | 'zh' | 'ja' | 'pt' | 'ar')
 * @returns {Object} - 언어별 필드만 포함된 객체
 */
function extractLanguageFields(data, language = 'ko') {
  const langSuffix = `_${language}`;

  // 기본 필드 (언어 무관)
  const baseFields = {
    id: data.id,
    errorCode: data.errorCode,
    plcModel: data.plcModel,
    category: data.category,
    scenarioIndex: data.scenarioIndex,
    productCategory: data.productCategory || data.productType, // productType fallback 지원
  };

  // 언어별 필드 매핑
  const languageFields = {
    // LS ELECTRIC 인버터: name_ko, name_en 등 (새로운 다국어 필드)
    // 미쓰비시/기타: errorName_ko 등
    errorName: data[`name${langSuffix}`] || data[`errorName${langSuffix}`] || data.name || data.errorName || '',
    errorMessage: data[`description${langSuffix}`] || data[`errorMessage${langSuffix}`] || data.errorMessage || '',
    description: data[`description${langSuffix}`] || data.description || '',
    cause: data[`cause${langSuffix}`] || data.cause || '',
    solution: data[`solution${langSuffix}`] || data.solution || '',

    // Motion 에러: errorAction이 실제로는 증상(symptom)임
    symptom: data[`errorAction${langSuffix}`] || data.errorAction || '',

    // 기존 호환성 유지 (추후 제거 예정)
    errorContent: data[`errorContent${langSuffix}`] || data.errorContent || '',
    errorAction: data[`errorAction${langSuffix}`] || data.errorAction || '',

    // LS ELECTRIC 인버터 추가 필드
    type: data.type || '',
    manufacturer: data.manufacturer || '',
    product: data.product || '',
    series: data.series || '',
  };

  // targets 배열이 있으면 언어별 필드 추출 적용
  if (data.targets && Array.isArray(data.targets)) {
    const processedTargets = data.targets.map(target => ({
      model: target[`model${langSuffix}`] || target.model || '',
      cases: target.cases ? target.cases.map(caseData => ({
        caseNumber: caseData.caseNumber,
        cause: caseData[`cause${langSuffix}`] || caseData.cause || '',
        symptom: caseData[`symptom${langSuffix}`] || caseData.symptom || '',
        solution: caseData[`solution${langSuffix}`] || caseData.solution || '',
      })) : []
    }));

    return { ...baseFields, ...languageFields, targets: processedTargets };
  }

  // cases 배열만 있는 경우 (104, 105 등)
  if (data.cases && Array.isArray(data.cases)) {
    const processedCases = data.cases.map(caseData => ({
      caseNumber: caseData.caseNumber,
      cause: caseData[`cause${langSuffix}`] || caseData.cause || '',
      symptom: caseData[`symptom${langSuffix}`] || caseData.symptom || '',
      solution: caseData[`solution${langSuffix}`] || caseData.solution || '',
    }));

    return { ...baseFields, ...languageFields, cases: processedCases };
  }

  return { ...baseFields, ...languageFields };
}

/**
 * Document ID로 에러 정보 조회 (단일 문서)
 * @param {string} docId - Firestore document ID
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} language - 언어 코드 ('ko' | 'en' | 'zh' | 'ja' | 'pt' | 'ar')
 * @returns {Promise<Object|null>} - 에러 정보 객체 또는 null
 */
export async function getErrorByDocId(docId, manufacturerId = 'mitsubishi', productCategory = 'plc', language = 'ko') {
  try {
    const errorsRef = getErrorsCollectionRef(manufacturerId, productCategory);
    const docRef = doc(errorsRef, docId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = {
      id: docSnap.id,
      ...docSnap.data(),
    };

    return extractLanguageFields(data, language);
  } catch (error) {
    console.error('Document ID로 에러 조회 실패:', error);
    throw error;
  }
}

/**
 * 에러 코드 검색 (모든 시나리오 반환)
 * @param {string} errorCode - 검색할 에러 코드 (예: "1000")
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'all')
 * @param {string} language - 언어 코드 ('ko' | 'en' | 'zh' | 'ja' | 'pt' | 'ar')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<Array>} - 에러 정보 배열 (여러 시나리오)
 */
export async function searchErrorCode(errorCode, productCategory = 'plc', language = 'ko', manufacturerId = 'mitsubishi') {
  try {
    const upperCode = errorCode.toUpperCase();

    // 🚀 최적화: 통합 검색 시 병렬 처리 (Promise.all)
    if (productCategory === 'all') {
      // 제조사별 제품 카테고리 결정
      const categories = manufacturerId === 'ls-electric'
        ? ['plc', 'motion', 'servo', 'inverter'] // LS ELECTRIC은 PLC + 모션 + 서보 + 인버터
        : ['plc', 'motion', 'servo', 'inverter']; // 미쓰비시도 4가지 (PLC + 모션 + 서보 + 인버터)

      const promises = categories.map(cat =>
        searchErrorCode(upperCode, cat, language, manufacturerId)
      );

      const results = await Promise.all(promises);
      return results.flat();
    }

    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const errorsRef = getErrorsCollectionRef(manufacturerId, productCategory);

    // 에러 코드로 직접 검색 (모든 제조사 동일)
    const q = query(errorsRef, where('errorCode', '==', upperCode));

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return []; // 빈 배열 반환
    }

    // 🌐 언어별 필드 추출 적용
    return snapshot.docs.map(doc => {
      const data = {
        id: doc.id,
        ...doc.data(),
      };
      return extractLanguageFields(data, language);
    });
  } catch (error) {
    console.error('에러 코드 검색 실패:', error);
    throw error;
  }
}

/**
 * 카테고리별 전체 에러 코드 조회
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} language - 언어 코드 ('ko' | 'en' | 'zh' | 'ja' | 'pt' | 'ar')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<Array>} - 전체 에러 코드 목록
 */
export async function getAllErrorsByCategory(productCategory = 'plc', language = 'ko', manufacturerId = 'mitsubishi') {
  try {
    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const errorsRef = getErrorsCollectionRef(manufacturerId, productCategory);

    // 에러 코드 기준 정렬 (모든 제조사 동일)
    const q = query(errorsRef, orderBy('errorCode', 'asc'));

    const snapshot = await getDocs(q);

    // 🌐 언어별 필드 추출 적용
    return snapshot.docs.map(doc => {
      const data = {
        id: doc.id,
        ...doc.data(),
      };
      return extractLanguageFields(data, language);
    });
  } catch (error) {
    console.error('전체 에러 조회 실패:', error);
    throw error;
  }
}

/**
 * 에러 코드 스마트 검색 (정확히 일치 + 서브코드)
 * @param {string} prefix - 검색할 접두사
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo' | 'all')
 * @param {string} language - 언어 코드 ('ko' | 'en' | 'zh' | 'ja' | 'pt' | 'ar')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<Array>} - 매칭되는 에러 코드 목록
 *
 * @example
 * "10" 검색 → "10", "10.1", "10.2" (O) / "100", "1000" (X)
 * "100" 검색 → "100", "100.1", "100.2" (O) / "1000", "1001" (X)
 */
export async function searchErrorCodesByPrefix(prefix, productCategory = 'plc', language = 'ko', manufacturerId = 'mitsubishi') {
  try {
    const upperPrefix = prefix.toUpperCase();

    // 🚀 최적화: 통합 검색 시 병렬 처리 (Promise.all)
    if (productCategory === 'all') {
      // 제조사별 제품 카테고리 결정
      const categories = manufacturerId === 'ls-electric'
        ? ['plc', 'motion', 'servo', 'inverter'] // LS ELECTRIC은 PLC + 모션 + 서보 + 인버터
        : ['plc', 'motion', 'servo', 'inverter']; // 미쓰비시도 4가지 (PLC + 모션 + 서보 + 인버터)

      const promises = categories.map(cat =>
        searchErrorCodesByPrefix(upperPrefix, cat, language, manufacturerId)
      );

      const results = await Promise.all(promises);
      return results.flat();
    }

    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const errorsRef = getErrorsCollectionRef(manufacturerId, productCategory);

    // errorCode 필드로 prefix 검색 (모든 제조사 동일)
    const q = query(
      errorsRef,
      where('errorCode', '>=', upperPrefix),
      where('errorCode', '<=', upperPrefix + '\uf8ff'),
      orderBy('errorCode', 'asc')
    );

    const snapshot = await getDocs(q);

    // 🎯 스마트 필터링: 정확히 일치하거나 서브코드(점 포함)만 허용
    // 예: "100" 검색 시 → "100", "100.1", "100.2" (O) / "1000", "1001" (X)
    const results = snapshot.docs
      .map(doc => {
        const data = {
          id: doc.id,
          ...doc.data(),
        };
        return extractLanguageFields(data, language);
      })
      .filter(result => {
        const errorCode = result.errorCode;
        // 정확히 일치하거나, "prefix." 로 시작하는 경우만 포함
        return errorCode === upperPrefix || errorCode.startsWith(upperPrefix + '.');
      });

    return results;
  } catch (error) {
    console.error('에러 코드 부분 검색 실패:', error);
    throw error;
  }
}

/**
 * 해결 후기 목록 조회
 * @param {string} errorCode - 에러 코드
 * @param {string} sortBy - 정렬 방식 ('best' | 'latest')
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<Array>} - 해결 후기 목록
 */
export async function getSolutions(errorCode, sortBy = 'best', productCategory = 'plc', manufacturerId = 'mitsubishi') {
  try {
    const upperCode = errorCode.toUpperCase();
    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const solutionsRef = getReviewsCollectionRef(manufacturerId, productCategory);

    // 정렬 방식 설정
    let q;
    if (sortBy === 'best') {
      // 베스트 순 (좋아요 많은 순)
      q = query(
        solutionsRef,
        where('errorCode', '==', upperCode),
        where('isDeleted', '==', false),
        orderBy('likes', 'desc')
      );
    } else {
      // 최신 순
      q = query(
        solutionsRef,
        where('errorCode', '==', upperCode),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('해결 후기 조회 실패:', error);
    throw error;
  }
}

/**
 * 해결 후기 작성
 * @param {string} errorCode - 에러 코드
 * @param {string} content - 후기 내용
 * @param {string} passwordHash - 해시된 비밀번호
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @param {string} docId - 에러 문서 ID (에러 상세 페이지 링크용)
 * @returns {Promise<string>} - 생성된 후기 ID
 */
export async function addSolution(errorCode, content, passwordHash, productCategory = 'plc', manufacturerId = 'mitsubishi', docId = null) {
  try {
    const upperCode = errorCode.toUpperCase();
    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const solutionsRef = getReviewsCollectionRef(manufacturerId, productCategory);

    const docRef = await addDoc(solutionsRef, {
      errorCode: upperCode,
      content: content.trim(),
      passwordHash: passwordHash,
      likes: 0,
      createdAt: serverTimestamp(),
      isDeleted: false,
      docId: docId, // 에러 문서 ID 저장
    });

    return docRef.id;
  } catch (error) {
    console.error('해결 후기 작성 실패:', error);
    throw error;
  }
}

/**
 * 해결 후기 수정 (Firestore 클라이언트 SDK 사용)
 * @param {string} errorCode - 에러 코드 (호환성 유지용)
 * @param {string} solutionId - 후기 ID
 * @param {string} content - 수정할 내용
 * @param {string} password - 비밀번호 (평문)
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function updateSolution(errorCode, solutionId, content, password, productCategory = 'plc', manufacturerId = 'mitsubishi') {
  try {
    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const reviewsRef = getReviewsCollectionRef(manufacturerId, productCategory);

    // 해당 리뷰 문서 참조
    const solutionRef = doc(reviewsRef, solutionId);

    // 문서 조회
    const solutionSnap = await getDoc(solutionRef);

    // 문서 존재 여부 확인
    if (!solutionSnap.exists()) {
      throw new Error('해결 후기를 찾을 수 없습니다.');
    }

    const solutionData = solutionSnap.data();

    // 🔒 bcrypt.compare로 비밀번호 검증 (해시 직접 비교 X)
    const isMatch = await bcrypt.compare(password, solutionData.passwordHash);

    if (!isMatch) {
      throw new Error('비밀번호가 일치하지 않습니다.');
    }

    // 내용 업데이트
    await updateDoc(solutionRef, {
      content: content.trim(),
    });

    console.log('✅ 해결 후기 수정 성공:', solutionId);
    return { success: true, message: '해결 후기가 수정되었습니다.' };
  } catch (error) {
    console.error('❌ 해결 후기 수정 실패:', error);

    // 이미 메시지가 있는 에러는 그대로 throw
    if (error.message) {
      throw error;
    }

    throw new Error('해결 후기 수정에 실패했습니다.');
  }
}

/**
 * 해결 후기 삭제 (Firestore 클라이언트 SDK 사용 - 소프트 삭제)
 * @param {string} errorCode - 에러 코드 (호환성 유지용)
 * @param {string} solutionId - 후기 ID
 * @param {string} password - 비밀번호 (평문)
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} manufacturerId - 제조사 ID ('mitsubishi' | 'ls-electric')
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function deleteSolution(errorCode, solutionId, password, productCategory = 'plc', manufacturerId = 'mitsubishi') {
  try {
    // 🚀 최적화: 제조사별 캐싱된 컬렉션 참조 사용
    const reviewsRef = getReviewsCollectionRef(manufacturerId, productCategory);

    // 해당 리뷰 문서 참조
    const solutionRef = doc(reviewsRef, solutionId);

    // 문서 조회
    const solutionSnap = await getDoc(solutionRef);

    // 문서 존재 여부 확인
    if (!solutionSnap.exists()) {
      throw new Error('해결 후기를 찾을 수 없습니다.');
    }

    const solutionData = solutionSnap.data();

    // 🔒 비밀번호 확인 (관리자는 빈 비밀번호로 우회)
    if (password && password.trim() !== '') {
      // 일반 사용자: 비밀번호 검증
      const isMatch = await bcrypt.compare(password, solutionData.passwordHash);

      if (!isMatch) {
        throw new Error('비밀번호가 일치하지 않습니다.');
      }
    }
    // 빈 비밀번호 = 관리자 삭제 (비밀번호 검증 건너뜀)

    // 소프트 삭제 (isDeleted: true로 변경)
    await updateDoc(solutionRef, {
      isDeleted: true,
    });

    console.log('✅ 해결 후기 삭제 성공:', solutionId);
    return { success: true, message: '해결 후기가 삭제되었습니다.' };
  } catch (error) {
    console.error('❌ 해결 후기 삭제 실패:', error);

    // 이미 메시지가 있는 에러는 그대로 throw
    if (error.message) {
      throw error;
    }

    throw new Error('해결 후기 삭제에 실패했습니다.');
  }
}

/**
 * 좋아요 추가
 * @param {string} errorCode - 에러 코드 (사용하지 않음, 호환성 유지)
 * @param {string} solutionId - 후기 ID
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion')
 * @returns {Promise<void>}
 */
export async function addLike(errorCode, solutionId, productCategory = 'plc') {
  try {
    const collectionName = getReviewsCollectionName(productCategory);
    const solutionRef = doc(db, collectionName, solutionId);

    await updateDoc(solutionRef, {
      likes: increment(1), // 좋아요 +1
    });
  } catch (error) {
    console.error('좋아요 추가 실패:', error);
    throw error;
  }
}

/**
 * 좋아요 취소
 * @param {string} errorCode - 에러 코드 (사용하지 않음, 호환성 유지)
 * @param {string} solutionId - 후기 ID
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion')
 * @returns {Promise<void>}
 */
export async function removeLike(errorCode, solutionId, productCategory = 'plc') {
  try {
    const collectionName = getReviewsCollectionName(productCategory);
    const solutionRef = doc(db, collectionName, solutionId);

    await updateDoc(solutionRef, {
      likes: increment(-1), // 좋아요 -1
    });
  } catch (error) {
    console.error('좋아요 취소 실패:', error);
    throw error;
  }
}

/**
 * 해결 후기 비밀번호 검증 (수정/삭제 전 검증용)
 * @param {string} solutionId - 후기 ID
 * @param {string} password - 평문 비밀번호
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @returns {Promise<boolean>} - 비밀번호 일치 여부
 */
export async function verifySolutionPassword(solutionId, password, productCategory = 'plc') {
  try {
    const reviewsCollectionName = getReviewsCollectionName(productCategory);
    const solutionRef = doc(db, reviewsCollectionName, solutionId);
    const solutionSnap = await getDoc(solutionRef);

    if (!solutionSnap.exists()) {
      throw new Error('해결 후기를 찾을 수 없습니다.');
    }

    const solutionData = solutionSnap.data();

    // 🔒 bcrypt.compare로 비밀번호 검증 (해시 직접 비교 X)
    const isMatch = await bcrypt.compare(password, solutionData.passwordHash);

    if (!isMatch) {
      throw new Error('비밀번호가 일치하지 않습니다.');
    }

    return true;
  } catch (error) {
    // 에러를 상위로 전달 (최종 처리하는 곳에서 로그)
    throw error;
  }
}

/**
 * 🔒 관리자 전용: 모든 제품의 전체 댓글 조회
 * @param {string} sortBy - 정렬 방식 ('latest' | 'likes')
 * @param {number} limit - 조회 개수 제한 (기본: 100)
 * @returns {Promise<Array>} - 전체 댓글 목록 (productCategory 포함)
 */
export async function getAllReviews(sortBy = 'latest', limit = 100) {
  try {
    // 모든 제조사와 제품 카테고리 조합 생성
    const manufacturers = [
      { id: 'mitsubishi', categories: ['plc', 'motion', 'servo', 'inverter'] },
      { id: 'ls-electric', categories: ['plc', 'motion', 'servo', 'inverter'] },
    ];

    const reviewsPromises = [];

    manufacturers.forEach(manufacturer => {
      manufacturer.categories.forEach(category => {
        const reviewsRef = reviewsCollectionRefs[manufacturer.id]?.[category];

        if (!reviewsRef) return; // 컬렉션이 없으면 스킵

        const promise = (async () => {
          // 복합 인덱스 불필요하도록 단순 쿼리 사용 (필터링/정렬은 JS에서)
          const snapshot = await getDocs(reviewsRef);

          return snapshot.docs
            .map(doc => ({
              id: doc.id,
              manufacturerId: manufacturer.id,
              productCategory: category,
              ...doc.data(),
            }))
            .filter(review => !review.isDeleted); // JS에서 삭제된 항목 필터링
        })();

        reviewsPromises.push(promise);
      });
    });

    // 모든 결과 병합
    const allReviews = (await Promise.all(reviewsPromises)).flat();

    // 정렬 (최신순 또는 좋아요순)
    if (sortBy === 'likes') {
      allReviews.sort((a, b) => {
        if (b.likes !== a.likes) {
          return b.likes - a.likes;
        }
        // 좋아요 같으면 최신순
        return b.createdAt?.toMillis() - a.createdAt?.toMillis();
      });
    } else {
      allReviews.sort((a, b) =>
        b.createdAt?.toMillis() - a.createdAt?.toMillis()
      );
    }

    // limit 적용
    return allReviews.slice(0, limit);
  } catch (error) {
    console.error('❌ 전체 댓글 조회 실패:', error);
    throw error;
  }
}

/**
 * 🔒 관리자 전용: 좋아요 숫자 직접 수정
 * @param {string} solutionId - 후기 ID
 * @param {number} newLikesCount - 새로운 좋아요 숫자
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 */
export async function updateLikesCount(solutionId, newLikesCount, productCategory = 'plc') {
  try {
    const reviewsCollectionName = getReviewsCollectionName(productCategory);
    const solutionRef = doc(db, reviewsCollectionName, solutionId);

    await updateDoc(solutionRef, {
      likes: newLikesCount
    });

    console.log('✅ 좋아요 숫자 업데이트 성공:', solutionId, newLikesCount);
  } catch (error) {
    console.error('❌ 좋아요 숫자 업데이트 실패:', error);
    throw error;
  }
}

// =============================================
// 📝 피드백/제보 관련 함수
// =============================================

/**
 * 특정 에러 코드의 피드백 조회
 * @param {string} errorCode - 에러 코드
 * @param {string} productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @returns {Promise<Array>} - 피드백 목록 (최신순)
 */
export async function getFeedbackByError(errorCode, productCategory = 'plc') {
  try {
    const upperCode = errorCode.toUpperCase();

    const q = query(
      feedbackCollectionRef,
      where('errorCode', '==', upperCode),
      where('productCategory', '==', productCategory),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('❌ 피드백 조회 실패:', error);
    throw error;
  }
}

/**
 * 🔒 관리자 전용: 모든 피드백 조회
 * @param {number} limit - 조회 개수 제한 (기본: 100)
 * @returns {Promise<Array>} - 전체 피드백 목록 (최신순)
 */
export async function getAllFeedback(limit = 100) {
  try {
    const q = query(
      feedbackCollectionRef,
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);

    const allFeedback = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter(feedback => !feedback.isDeleted); // 삭제되지 않은 피드백만

    return allFeedback.slice(0, limit);
  } catch (error) {
    console.error('❌ 전체 피드백 조회 실패:', error);
    throw error;
  }
}

/**
 * 피드백 추가 (익명 제보)
 * @param {Object} data - 피드백 데이터
 * @param {string} data.errorCode - 에러 코드
 * @param {string} data.productCategory - 제품 카테고리 ('plc' | 'motion' | 'servo')
 * @param {string} data.manufacturer - 제조사 ('mitsubishi' | 'ls-electric')
 * @param {string} data.docId - 에러 문서 ID
 * @param {string} data.title - 제목
 * @param {string} data.content - 내용
 * @returns {Promise<string>} - 생성된 피드백 ID
 */
export async function addFeedback({ errorCode, productCategory, manufacturer, docId, title, content }) {
  try {
    const upperCode = errorCode.toUpperCase();

    // 1. 피드백 저장
    const docRef = await addDoc(feedbackCollectionRef, {
      errorCode: upperCode,
      productCategory: productCategory,
      manufacturer: manufacturer,
      docId: docId,
      title: title.trim(),
      content: content.trim(),
      status: 'pending', // pending | resolved
      createdAt: serverTimestamp(),
    });

    // 2. 이메일 알림 발송 (Firebase Trigger Email Extension)
    const ADMIN_EMAIL = 'makerssomac@gmail.com';

    // 에러 상세 페이지 링크 생성
    const errorPageUrl = manufacturer && docId
      ? `https://plc-error-guide.web.app/manufacturer/${manufacturer}/${productCategory}/error/${upperCode}/${docId}`
      : null;

    try {
      await addDoc(collection(db, 'mail'), {
        to: ADMIN_EMAIL,
        message: {
          subject: `[PLC Error Guide] 새 피드백: ${title.trim()}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #1976d2;">📬 새 피드백이 접수되었습니다</h2>
              <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">제조사</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${manufacturer || '-'}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">에러 코드</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${upperCode}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">제품 카테고리</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${productCategory}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">제목</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${title.trim()}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">내용</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${content.trim()}</td>
                </tr>
              </table>
              ${errorPageUrl ? `
              <div style="margin: 20px 0; text-align: center;">
                <a href="${errorPageUrl}" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
                  🔗 에러 상세 페이지 바로가기
                </a>
              </div>
              ` : ''}
              <p style="color: #666; font-size: 12px;">
                이 이메일은 PLC Error Guide 앱에서 자동 발송되었습니다.
              </p>
            </div>
          `,
        },
      });
      console.log('📧 이메일 알림 발송 요청 완료');
    } catch (emailError) {
      // 이메일 실패해도 피드백 저장은 성공으로 처리
      console.warn('⚠️ 이메일 알림 발송 실패 (피드백은 저장됨):', emailError);
    }

    console.log('✅ 피드백 추가 성공:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ 피드백 추가 실패:', error);
    throw error;
  }
}

/**
 * 개발자에게 문의하기 (앱 전체 문의)
 * @param {Object} params - 문의 데이터
 * @param {string} params.category - 문의 유형 (general | bug | feature | data | other)
 * @param {string} params.title - 제목
 * @param {string} params.content - 내용
 * @param {string|null} params.email - 답변받을 이메일 (선택)
 * @returns {Promise<string>} - 문의 ID
 */
export async function addContactInquiry({ category, title, content, email }) {
  try {
    // 1. 문의 저장 (feedback 컬렉션에 type으로 구분)
    const docRef = await addDoc(feedbackCollectionRef, {
      type: 'contact', // 'feedback'과 구분하기 위한 타입
      category: category,
      title: title.trim(),
      content: content.trim(),
      email: email || null,
      status: 'pending', // pending | resolved
      createdAt: serverTimestamp(),
    });

    // 2. 이메일 알림 발송 (Firebase Trigger Email Extension)
    const ADMIN_EMAIL = 'makerssomac@gmail.com';

    // 카테고리 한글 변환
    const categoryLabels = {
      general: '일반 문의',
      bug: '버그 신고',
      feature: '기능 제안',
      data: '데이터 오류',
      other: '기타',
    };

    try {
      await addDoc(collection(db, 'mail'), {
        to: ADMIN_EMAIL,
        message: {
          subject: `[PLC Error Guide] 개발자 문의: ${title.trim()}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #1976d2;">📬 새 개발자 문의가 접수되었습니다</h2>
              <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">문의 유형</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${categoryLabels[category] || category}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">제목</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${title.trim()}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">내용</td>
                  <td style="padding: 10px; border: 1px solid #ddd; white-space: pre-wrap;">${content.trim()}</td>
                </tr>
                <tr>
                  <td style="padding: 10px; border: 1px solid #ddd; background: #f5f5f5; font-weight: bold;">답변 이메일</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">${email || '(미입력)'}</td>
                </tr>
              </table>
              <p style="color: #666; font-size: 12px;">
                이 이메일은 PLC Error Guide 앱에서 자동 발송되었습니다.
              </p>
            </div>
          `,
        },
      });
      console.log('📧 개발자 문의 이메일 발송 요청 완료');
    } catch (emailError) {
      // 이메일 실패해도 문의 저장은 성공으로 처리
      console.warn('⚠️ 이메일 알림 발송 실패 (문의는 저장됨):', emailError);
    }

    console.log('✅ 개발자 문의 추가 성공:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ 개발자 문의 추가 실패:', error);
    throw error;
  }
}

/**
 * 🔒 관리자 전용: 피드백 삭제
 * @param {string} feedbackId - 피드백 ID
 * @returns {Promise<void>}
 */
export async function deleteFeedback(feedbackId) {
  try {
    const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);

    // 문서 존재 확인
    const feedbackSnap = await getDoc(feedbackRef);
    if (!feedbackSnap.exists()) {
      throw new Error('피드백을 찾을 수 없습니다.');
    }

    // 문서 삭제 (하드 삭제)
    await updateDoc(feedbackRef, {
      isDeleted: true, // 소프트 삭제
    });

    console.log('✅ 피드백 삭제 성공:', feedbackId);
  } catch (error) {
    console.error('❌ 피드백 삭제 실패:', error);
    throw error;
  }
}

/**
 * 🔒 관리자 전용: 피드백 상태 변경
 * @param {string} feedbackId - 피드백 ID
 * @param {string} status - 상태 ('pending' | 'resolved')
 * @returns {Promise<void>}
 */
export async function updateFeedbackStatus(feedbackId, status) {
  try {
    const feedbackRef = doc(db, FEEDBACK_COLLECTION, feedbackId);

    await updateDoc(feedbackRef, {
      status: status,
    });

    console.log('✅ 피드백 상태 변경 성공:', feedbackId, status);
  } catch (error) {
    console.error('❌ 피드백 상태 변경 실패:', error);
    throw error;
  }
}

/**
 * 🔒 관리자 전용: LS ELECTRIC Inverter 문서에 productCategory 필드 추가
 * @returns {Promise<number>} - 업데이트된 문서 개수
 */
export async function fixInverterProductCategory() {
  try {
    const collectionRef = collection(db, 'ls_electric_inverter_errors');
    const snapshot = await getDocs(collectionRef);

    console.log(`총 ${snapshot.size}개 문서 발견`);

    const batchWrites = [];
    const BATCH_SIZE = 500; // Firestore batch 제한
    let currentBatch = null;
    let operationCount = 0;

    for (const docSnapshot of snapshot.docs) {
      if (operationCount % BATCH_SIZE === 0) {
        if (currentBatch) {
          batchWrites.push(currentBatch.commit());
        }
        currentBatch = writeBatch(db);
      }

      currentBatch.update(docSnapshot.ref, { productCategory: 'inverter' });
      operationCount++;
    }

    if (currentBatch) {
      batchWrites.push(currentBatch.commit());
    }

    await Promise.all(batchWrites);
    console.log(`✅ ${operationCount}개 문서에 productCategory: 'inverter' 추가 완료`);
    return operationCount;
  } catch (error) {
    console.error('❌ productCategory 업데이트 실패:', error);
    throw error;
  }
}
